import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import React, { useMemo, useState } from "react";
import ExpiryMedicationCard, { ExpiryItemData, ExpiryStatus } from "./expiry-medication-card";

// Helper function to calculate expiry status
const calculateExpiryStatus = (expiryDate: string): { status: ExpiryStatus; days: number } => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
        return { status: ExpiryStatus.EXPIRED, days: daysDiff };
    } else if (daysDiff <= 30) {
        return { status: ExpiryStatus.EXPIRING_SOON, days: daysDiff };
    } else {
        return { status: ExpiryStatus.SAFE, days: daysDiff };
    }
};

const Component: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState("");

    // Sample data with calculated expiry status
    const expiryItems: ExpiryItemData[] = [
        {
            name: "Amoxicillin 500mg",
            batch_no: "BATCH-2024-001",
            quantity: 45,
            location: "Shelf A1",
            expiry_date: "2025-01-05",
            ...calculateExpiryStatus("2025-01-05")
        },
        {
            name: "Paracetamol 500mg",
            batch_no: "BATCH-2024-002",
            quantity: 120,
            location: "Shelf B2",
            expiry_date: "2025-11-15",
            ...calculateExpiryStatus("2025-11-15")
        },
        {
            name: "Ibuprofen 400mg",
            batch_no: "BATCH-2024-003",
            quantity: 80,
            location: "Shelf C3",
            expiry_date: "2025-12-28",
            ...calculateExpiryStatus("2025-12-28")
        },
        {
            name: "Aspirin 100mg",
            batch_no: "BATCH-2024-004",
            quantity: 200,
            location: "Shelf D1",
            expiry_date: "2026-06-15",
            ...calculateExpiryStatus("2026-06-15")
        },
        {
            name: "Metformin 850mg",
            batch_no: "BATCH-2024-005",
            quantity: 60,
            location: "Shelf E2",
            expiry_date: "2025-12-01",
            ...calculateExpiryStatus("2025-12-01")
        },
        {
            name: "Omeprazole 20mg",
            batch_no: "BATCH-2024-006",
            quantity: 90,
            location: "Shelf F3",
            expiry_date: "2025-11-30",
            ...calculateExpiryStatus("2025-11-30")
        },
        {
            name: "Lisinopril 10mg",
            batch_no: "BATCH-2024-007",
            quantity: 75,
            location: "Shelf A2",
            expiry_date: "2026-03-20",
            ...calculateExpiryStatus("2026-03-20")
        },
        {
            name: "Atorvastatin 20mg",
            batch_no: "BATCH-2024-008",
            quantity: 110,
            location: "Shelf B1",
            expiry_date: "2025-12-30",
            ...calculateExpiryStatus("2025-12-30")
        },
        {
            name: "Losartan 50mg",
            batch_no: "BATCH-2024-009",
            quantity: 95,
            location: "Shelf C2",
            expiry_date: "2025-11-10",
            ...calculateExpiryStatus("2025-11-10")
        },
        {
            name: "Furosemide 40mg",
            batch_no: "BATCH-2024-010",
            quantity: 55,
            location: "Shelf D2",
            expiry_date: "2026-02-15",
            ...calculateExpiryStatus("2026-02-15")
        },
        {
            name: "Warfarin 5mg",
            batch_no: "BATCH-2024-011",
            quantity: 30,
            location: "Shelf E1",
            expiry_date: "2025-12-20",
            ...calculateExpiryStatus("2025-12-20")
        },
        {
            name: "Digoxin 0.25mg",
            batch_no: "BATCH-2024-012",
            quantity: 25,
            location: "Shelf F1",
            expiry_date: "2025-10-15",
            ...calculateExpiryStatus("2025-10-15")
        },
        {
            name: "Ramipril 5mg",
            batch_no: "BATCH-2024-013",
            quantity: 110,
            location: "Shelf A3",
            expiry_date: "2026-08-10",
            ...calculateExpiryStatus("2026-08-10")
        },
        {
            name: "Simvastatin 40mg",
            batch_no: "BATCH-2024-014",
            quantity: 65,
            location: "Shelf B3",
            expiry_date: "2025-12-25",
            ...calculateExpiryStatus("2025-12-25")
        },
        {
            name: "Amlodipine 5mg",
            batch_no: "BATCH-2024-015",
            quantity: 85,
            location: "Shelf C1",
            expiry_date: "2025-11-05",
            ...calculateExpiryStatus("2025-11-05")
        }
    ].map(item => ({
        ...item,
        status: calculateExpiryStatus(item.expiry_date).status,
        days_until_expiry: calculateExpiryStatus(item.expiry_date).days
    }));

    // Filter items based on search term
    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) {
            return expiryItems;
        }

        const lowercaseSearch = searchTerm.toLowerCase();
        return expiryItems.filter(item => 
            item.name.toLowerCase().includes(lowercaseSearch) ||
            item.batch_no.toLowerCase().includes(lowercaseSearch) ||
            item.location.toLowerCase().includes(lowercaseSearch) ||
            item.status.toLowerCase().includes(lowercaseSearch)
        );
    }, [searchTerm, expiryItems]);

    // Sort items by priority: Expired first, then expiring soon, then safe
    const sortedItems = useMemo(() => {
        return [...filteredItems].sort((a, b) => {
            const statusOrder = { [ExpiryStatus.EXPIRED]: 0, [ExpiryStatus.EXPIRING_SOON]: 1, [ExpiryStatus.SAFE]: 2 };
            if (statusOrder[a.status] !== statusOrder[b.status]) {
                return statusOrder[a.status] - statusOrder[b.status];
            }
            // Within same status, sort by days until expiry
            return a.days_until_expiry - b.days_until_expiry;
        });
    }, [filteredItems]);

    return (
        <div className="flex flex-col items-start justify-start p-4 w-full">
            <Card className="bg-[#FFFFFF] relative border rounded-2xl w-full">
                <CardContent className="w-full p-6 flex flex-col items-start justify-start gap-y-2">
                    <span className="font-semibold text-xl">Expiry Date Tracking</span>
                    <span className="font-normal text-base text-gray-400 mb-4">Monitor medications expiring soon and non-moving stock</span>
                    <div className="flex flex-row items-center justify-between w-full mb-4 gap-x-4">
                        <Input 
                            placeholder="Search medication, batch, location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Button>Export Report</Button>
                    </div>
                    {sortedItems.map((item, index) => (
                        <ExpiryMedicationCard key={index} {...item} />
                    ))}
                </CardContent>
            </Card>
        </div>
    );
}

export default Component;